import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalPath: string;
  schemaData?: object;
  ogImage?: string;
}

export default function SEOHead({ title, description, keywords, canonicalPath, schemaData, ogImage }: SEOHeadProps) {
  useEffect(() => {
    // Update title
    document.title = title;
    
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const fullUrl = `${siteUrl}${canonicalPath}`;
    
    // Update meta tags with enhanced SEO
    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'robots', content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' },
      { name: 'googlebot', content: 'index, follow' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: fullUrl },
      { property: 'og:type', content: 'website' },
      { property: 'og:locale', content: 'en_US' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
    ];
    
    // Add og:image if provided
    if (ogImage) {
      metaTags.push({ property: 'og:image', content: ogImage });
      metaTags.push({ name: 'twitter:image', content: ogImage });
    }
    
    metaTags.forEach(({ name, property, content }) => {
      const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
      let meta = document.querySelector(selector);
      
      if (!meta) {
        meta = document.createElement('meta');
        if (name) meta.setAttribute('name', name);
        if (property) meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      
      meta.setAttribute('content', content);
    });
    
    // Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);
    
    // Update last-modified
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);
    
    // Add Schema.org JSON-LD
    if (schemaData) {
      const existingScript = document.querySelector('script[type="application/ld+json"][data-page-schema]');
      if (existingScript) {
        existingScript.remove();
      }
      
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-page-schema', 'true');
      script.textContent = JSON.stringify(schemaData);
      document.head.appendChild(script);
    }
    
    // Add BreadcrumbList schema for better navigation understanding
    if (canonicalPath !== '/') {
      const pathSegments = canonicalPath.split('/').filter(Boolean);
      const breadcrumbItems = [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: siteUrl
        }
      ];
      
      pathSegments.forEach((segment, index) => {
        const name = segment.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        
        breadcrumbItems.push({
          '@type': 'ListItem',
          position: index + 2,
          name: name,
          item: `${siteUrl}/${pathSegments.slice(0, index + 1).join('/')}`
        });
      });
      
      const breadcrumbSchema = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems
      };
      
      const existingBreadcrumb = document.querySelector('script[type="application/ld+json"][data-breadcrumb]');
      if (existingBreadcrumb) {
        existingBreadcrumb.remove();
      }
      
      const breadcrumbScript = document.createElement('script');
      breadcrumbScript.setAttribute('type', 'application/ld+json');
      breadcrumbScript.setAttribute('data-breadcrumb', 'true');
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(breadcrumbScript);
    }
    
    return () => {
      // Cleanup on unmount
      const pageSchema = document.querySelector('script[type="application/ld+json"][data-page-schema]');
      if (pageSchema) {
        pageSchema.remove();
      }
      const breadcrumb = document.querySelector('script[type="application/ld+json"][data-breadcrumb]');
      if (breadcrumb) {
        breadcrumb.remove();
      }
    };
  }, [title, description, keywords, canonicalPath, schemaData, ogImage]);
  
  return null;
}
